import ExpandingCard from "common/Boxes/ExpandingCard";
import Col from "antd/es/col";
import Row from "antd/es/row";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import React from "react";
import { MarketTableType } from "types";
import styled, { useTheme } from "styled-components";
import PriceChange from "common/Charts/PriceChange";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import PlusCircleOutlined from "@ant-design/icons/PlusCircleOutlined";
import { getCoinExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import ExtraInfoItem from "common/ExtraDetails/ExtraInfoItem";
import CardActions from "common/Lists/CardsList/CardActions";
import { DISPLAY_CURRENCIES } from "common/constants";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import AutoSizeText from "common/Text/AutoSizeText";
import { typography } from "@shared/common/Themes/typography.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces, PrecisionType } from "@shared/types";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { toFixed } from "@shared/utils/numbers";
import { getPricePrecision } from "@shared/features/portfolio/utils";
import StatAmount from "common/NewLook/StatAmount.tsx";
import { NewLookLiveChange } from "features/watchlist/AllMarkets/NewLookMarketCells.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface MarketCardProps {
  market: MarketTableType;
  columnSpans: number[];
  showExchangesModal: (symbol: string) => void;
  toggleExpanded: (key: string | number) => void;
  isExpanded: boolean;
}

const MarketCard = ({
  market,
  columnSpans,
  showExchangesModal,
  toggleExpanded,
  isExpanded,
}: MarketCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.WATCHLIST);
  const theme = useTheme();
  const { historyPush } = useHistoryWithQuery();
  const displayCurrency = useSelector(selectDisplayCurrency);

  const getPriceByCurrency = (priceArray: [number, number] | undefined) => {
    if (priceArray && priceArray[0] !== null && priceArray[1] !== null)
      return (
        <TextWithIcon
          text={(displayCurrency === DISPLAY_CURRENCIES.USD
            ? priceArray[0]
            : priceArray[1]
          ).toLocaleString()}
          iconType={ICON_TYPES.DISPLAY_CURRENCY}
        />
      );
  };

  const extraInfoData = [
    {
      name: t("card.24h_low_price"),
      value: getPriceByCurrency(market.low_price),
    },
    {
      name: t("card.24h_high_price"),
      value: getPriceByCurrency(market.high_price),
    },
    {
      name: t("card.24h_volume"),
      value: getPriceByCurrency(market.volume),
    },
  ];

  const getExtraInfoActions = (market: MarketTableType) => {
    return [
      {
        icon: <InfoCircleOutlined />,
        text: t("card.coin_details"),
        onClick: () =>
          historyPush(
            getCoinExtraDetailsRoute(location.pathname || "", market.symbol)
          ),
      },
      {
        icon: <PlusCircleOutlined />,
        text: t("card.add_to_watchlist"),
        onClick: () => showExchangesModal(market.symbol),
      },
    ];
  };

  const collapsibleContent = (
    <div>
      {extraInfoData?.map((item, index) => {
        return (
          <ExtraInfoItem
            name={item.name.toString()}
            value={item.value}
            key={index}
            bordered={false}
            height={36}
            valueTextStyle={{
              textAlign: "right",
            }}
            nameWrapperStyle={{
              whiteSpace: "nowrap",
              fontSize: typography.fontSizes.S,
            }}
          />
        );
      })}
      <CardActions
        borderTop={true}
        actionsData={getExtraInfoActions?.(market)}
        size="m"
      />
    </div>
  );

  if (theme.isNewLook) {
    return (
      <ExpandingCard
        collapsibleContent={collapsibleContent}
        expanded={isExpanded}
        setExpanded={() => toggleExpanded(market.key)}
        style={{
          padding: "10px 14px",
          borderRadius: NEW_LOOK_RADIUS.panel,
          boxShadow: `inset 0 0 0 1px ${getNewLookTokens(theme).hairline}, ${
            theme.shadows.dashboardCard
          }`,
        }}
      >
        <Row style={{ alignItems: "center", minHeight: 36 }}>
          <Col span={columnSpans[0]}>
            <NewLookSymbol>
              <IconWithFallback
                coinOrExchangeName={market.symbol}
                iconType={ICON_TYPES.COIN}
                pxIconSize={24}
              />
              <span>{market.symbol}</span>
            </NewLookSymbol>
          </Col>
          <Col span={columnSpans[1]}>
            <NewLookPrice>
              <NewLookMarketPrice market={market} />
            </NewLookPrice>
          </Col>
          <Col span={columnSpans[2]}>
            {/* Starts on the column caption's edge, like price and symbol. */}
            <Flex $justifyContent="flex-start">
              {/* Same live change as the desktop table: coloured text. */}
              <NewLookLiveChange
                symbol={market.symbol}
                exchange={market.exchange}
                initialChange={market.graph_data.change}
              />
            </Flex>
          </Col>
        </Row>
      </ExpandingCard>
    );
  }

  return (
    <ExpandingCard
      collapsibleContent={collapsibleContent}
      expanded={isExpanded}
      setExpanded={() => toggleExpanded(market.key)}
      style={{ padding: "8px 12px" }}
    >
      <Row style={{ alignItems: "center" }}>
        <Col span={columnSpans[0]}>
          <Flex style={{ width: "100%", height: 40 }}>
            <IconWithFallback
              coinOrExchangeName={market.symbol}
              iconSize={"m"}
              iconType={ICON_TYPES.COIN}
              iconStyle={{ marginRight: 4 }}
            />
            <div style={{ marginRight: 4, width: "100%" }}>
              <AutoSizeText>{market.symbol}</AutoSizeText>
            </div>
          </Flex>
        </Col>
        <Col span={columnSpans[1]}>
          <TextWithIcon
            text={market.price}
            coinOrExchangeName={market.symbol?.split("/")[1]}
            iconType={ICON_TYPES.COIN}
            iconSize={"m"}
            style={{
              color: theme.colors.textColor,
              fontSize: theme.typography.fontSizes.S,
            }}
          />
        </Col>
        <Col span={columnSpans[2]}>
          <Flex $justifyContent="flex-end" style={{ marginRight: 12 }}>
            <PriceChange
              change={market.graph_data.change}
              size={theme.typography.pxFontSizes.S}
              useBackground={true}
              style={{ width: "fit-content" }}
            />
          </Flex>
        </Col>
      </Row>
    </ExpandingCard>
  );
};

export default React.memo(MarketCard);

/**
 * New look: the price at the pair's price precision (more digits when that
 * would round a tiny price to 0), "$" for dollar quotes, else the quote coin.
 */
const NewLookMarketPrice = ({ market }: { market: MarketTableType }) => {
  const { precision: symbolPrecision } = usePrecisions({
    symbol: market.symbol,
    exchange: market.exchange,
    precisionType: PrecisionType.PRICE,
  });
  const price = Number(market.price) || 0;
  const precision =
    price && !toFixed(price, symbolPrecision)
      ? getPricePrecision(price, DISPLAY_CURRENCIES.USD)
      : symbolPrecision;
  return (
    <StatAmount
      text={price}
      coin={market.symbol?.split("/")[1]}
      precision={precision}
    />
  );
};

const NewLookSymbol = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-right: 8px;
  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${(p) => p.theme.colors.titleColor};
    ${newLookType.panelTitle};
  }
`;
const NewLookPrice = styled.div`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
